import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";

const INIT_STATE = {
    loadingRiskByDriver: false,
    riskByDriver: null,
    errorRiskByDriver: null,
    loading: false,
    data: null,
    error: null,
    nextToken: null,
    loadingRiskEventDistribution: false,
    riskEventDistribution: null,
    errorRiskEventDistribution: null,
    loadingSeverityBreakdown: false,
    severityBreakdown: null,
    errorSeverityBreakdown: null,
}

const route = 'module/driverPerformance/views/driverDetail/components/overview/redux/slice'

export const getCaseByIdDriver = createAsyncThunk('driverPerformance/views/driverDetail/components/overview/getCaseByIdDriver', async (payload, { rejectWithValue, getState }) => {
    try {
        const { limit, conditions } = payload

        const state = getState();
        let pagination = payload.conditions.find((item) => item.page);
        const currentNextToken = pagination === 1 ? '' : state.driverPerformanceOverviewRedux.nextToken;
        const currentConditions = conditions.filter((item) => !item.page);

        let params = {
            limit,
            nextToken: currentNextToken || '',
            conditions: currentConditions,
            sort: payload?.sort || [{ field: 'completedDate', order: 'DESC' }],
        }

        const response = await clientQuery(
            getCaseByIdDriver.query,
            params,
            endpoints.GRAPHQL_GENERAL_V2
        );

        return response?.data ? response?.data : { items: [], nextToken: null };
    } catch (exc) {
        ExceptionManager(exc, route, 'getCaseByIdDriver');
        return rejectWithValue(exc);
    }

})

getCaseByIdDriver.query = `
                query searchDriverPerformanceCases ($conditions: [DriverPerformanceCaseSearchInput], $sort: [DriverPerformanceCaseSortInput], $limit: Int, $offset: Int, $nextToken: String) {
                    data: searchDriverPerformanceCases (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset, nextToken: $nextToken) {
                        items {
                            id                  
                            caseNumber
                            status {
                                entityId
                                label
                            }
                            createdOn
                        }
                        nextToken
                        total
                    }
                }`;


export const getRiskByDriver = createAsyncThunk('driverPerformance/views/driverDetail/components/overview/getRiskByDriver', async (payload, { rejectWithValue }) => {
    const { driverId, startDate, endDate } = payload;
    try {
        const response = await clientQuery(
            getRiskByDriver.query,
            {
                driverIds: [parseInt(driverId)],
                startDate: startDate,
                endDate: endDate,
            },
            endpoints.GRAPHQL_GENERAL_V2
        );
        return response?.data;
    }
    catch (exc) {
        ExceptionManager(exc, route, 'getRiskByDriver');
        return rejectWithValue(exc);
    }
});

getRiskByDriver.query = `
query searchDriverPerformanceDrivers(
    $startDate: AWSDateTime!
    $endDate: AWSDateTime!
    $driverIds: [Int]
) {
    data: searchDriverPerformanceDrivers(
        startDate: $startDate
        endDate: $endDate
        driverIds: $driverIds
    ) {
        total
        items {
            driverId
            driverName
            totalEvents
            totalDistance
            avgEvents
            riskEvents
            riskScore
            driverTags
            driverTagsNames
        }
    }
}`;

export const getEventSeverityBreakdown = createAsyncThunk(
    'driverPerformance/views/driverDetail/components/overview/getEventSeverityBreakdown',
    async (payload, { rejectWithValue }) => {
        const { driverId, startDate, endDate, eventName, tagIds } = payload;

        try {
            const response = await clientQuery(
                `
                    query GetEventSeverityBreakdown(
                        $startDate: AWSDateTime!
                        $endDate: AWSDateTime!
                        $eventName: String!
                        $driverIds: [Int]
                        $tagIds: [Int]
                    ) {
                        data: getEventSeverityBreakdown(
                            startDate: $startDate
                            endDate: $endDate
                            eventName: $eventName
                            driverIds: $driverIds
                            tagIds: $tagIds
                        ) {
                            critical
                            high
                            medium
                            low
                        }
                    }
                `,
                {
                    startDate,
                    endDate,
                    eventName,
                    driverIds: driverId ? [parseInt(driverId)] : null,
                    tagIds: tagIds || null
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data || { critical: 0, high: 0, medium: 0, low: 0 };
        } catch (exc) {
            ExceptionManager(exc, route, 'getEventSeverityBreakdown');
            return rejectWithValue(exc);
        }
    }
);

export const getRiskEventDistribution = createAsyncThunk(
    'driverPerformance/views/driverDetail/components/overview/getRiskEventDistribution',
    async (payload, { rejectWithValue }) => {
        const { driverId, startDate, endDate } = payload;

        try {

            let params = {
                "conditions": [
                    {
                        "field": "driverIds",
                        "terms": [driverId]
                    }
                ],
                "gadgetKey": "RiskEventDistribution",
                "timeFrame": {
                    "GTE": startDate,
                    "LTE": endDate
                },
                "sort": null,
                "limit": 25,
                "offset": 0
            }

            const response = await clientQuery(
                `
                    query (
                        $gadgetKey: enumDashboardGadgetKey!
                        $timeFrame: RangeInput
                        $conditions: [GadgetDataSearchInput]
                        $sort: GadgetDataSortInput
                        $limit: Int
                        $offset: Int
                        $nextToken: String
                    ) {
                        data: searchDashboardGadgetData(
                            gadgetKey: $gadgetKey
                            timeFrame: $timeFrame
                            limit: $limit
                            offset: $offset
                            conditions: $conditions
                            sort: $sort
                            limit: $limit
                            offset: $offset
                            nextToken: $nextToken
                        ) {
                            total
                            items
                            nextToken
                        }
                    }
                `,
                params,
                endpoints.GRAPHQL_GENERAL_V2
            );

            let data = {}
            if (response?.data?.items) {
                try {
                    let items = JSON.parse(response.data.items);
                    data = {
                        items: Array.isArray(items) ? items : [],
                        total: response.data.total || 0,
                        nextToken: response.data.nextToken || null
                    }
                } catch (parseError) {
                    data = { items: [], total: 0, nextToken: null }
                }
            } else {
                data = { items: [], total: 0, nextToken: null }
            }

            return { data: data };
        } catch (exc) {
            ExceptionManager(exc, route, 'getRiskEventDistribution');
            return rejectWithValue(exc);
        }
    }
);

const driverPerformanceOverviewRedux = createSlice({
    name: 'driverPerformance/views/driverDetail/components/overview',
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
    },
    extraReducers: (builder) => {
        //getCaseByIdDriver
        builder.addCase(getCaseByIdDriver.pending, (state, action) => {
            const { conditions } = action.meta.arg
            let pagination = conditions.find((item) => item.page);

            if (pagination?.page === 1) {
                state.data = []
                state.nextToken = null
            }

            state.loading = true;
        });
        builder.addCase(getCaseByIdDriver.fulfilled, (state, action) => {
            const newItems = action.payload?.items || [];
            const nextToken = action.payload?.nextToken || null;
            const itemsWithCountId = newItems.map((item, index) => ({
                ...item,
                countId: state.data.length + index + 1
            }));

            state.data = [...state.data, ...itemsWithCountId]
            state.nextToken = nextToken
            state.loading = false;
        });
        builder.addCase(getCaseByIdDriver.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //getRiskByDriver
        builder.addCase(getRiskByDriver.pending, (state) => {
            state.loadingRiskByDriver = true;
        });
        builder.addCase(getRiskByDriver.fulfilled, (state, action) => {
            state.loadingRiskByDriver = false;
            state.riskByDriver = action.payload;
        });
        builder.addCase(getRiskByDriver.rejected, (state, action) => {
            state.loadingRiskByDriver = false;
            state.errorRiskByDriver = action.payload;
        });

        //getRiskEventDistribution
        builder.addCase(getRiskEventDistribution.pending, (state) => {
            state.loadingRiskEventDistribution = true;
        });
        builder.addCase(getRiskEventDistribution.fulfilled, (state, action) => {
            state.loadingRiskEventDistribution = false;
            state.riskEventDistribution = action.payload;
        });
        builder.addCase(getRiskEventDistribution.rejected, (state, action) => {
            state.loadingRiskEventDistribution = false;
            state.errorRiskEventDistribution = action.payload;
        });

        //getEventSeverityBreakdown
        builder.addCase(getEventSeverityBreakdown.pending, (state) => {
            state.loadingSeverityBreakdown = true;
        });
        builder.addCase(getEventSeverityBreakdown.fulfilled, (state, action) => {
            state.loadingSeverityBreakdown = false;
            state.severityBreakdown = action.payload;
        });
        builder.addCase(getEventSeverityBreakdown.rejected, (state, action) => {
            state.loadingSeverityBreakdown = false;
            state.errorSeverityBreakdown = action.payload;
        });
    },
})

export const { actions, reducer } = driverPerformanceOverviewRedux;

export const { reset } = actions;

export default reducer;
